import { inject, Injectable, NgZone } from '@angular/core';
import posthog, { CapturedNetworkRequest } from 'posthog-js';

export interface PosthogInitConfig {
  key: string;
  host: string;
  /** Source app, registered as the `app` super property to segment events. */
  app: string;
}

/**
 * App-agnostic PostHog wrapper shared across EnrollHere frontends.
 *
 * Hosts call `init()` with their environment key/host and an `app` tag, then
 * wire their own auth to `identify()`/`reset()` (and `register()`/`capture()`
 * for impersonation tracking). All calls run outside Angular's zone and no-op
 * until `init()` has run with a configured key + host.
 */
@Injectable({ providedIn: 'root' })
export class PosthogService {
  private readonly ngZone = inject(NgZone);

  private initialized = false;

  init(config: PosthogInitConfig): void {
    if (this.initialized || !config.key || !config.host) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      posthog.init(config.key, {
        api_host: config.host,
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        enable_heatmaps: true,
        capture_exceptions: true,
        capture_performance: { network_timing: true, web_vitals: true },
        enable_recording_console_log: false,
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: { password: true },
          recordHeaders: true,
          recordBody: true,
          maskCapturedNetworkRequestFn: request =>
            this.redactNetworkRequest(request),
        },
      });
      posthog.register({ app: config.app });
    });

    this.initialized = true;
  }

  identify(distinctId: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;
    this.ngZone.runOutsideAngular(() =>
      posthog.identify(distinctId, properties)
    );
  }

  reset(): void {
    if (!this.initialized) return;
    this.ngZone.runOutsideAngular(() => posthog.reset());
  }

  register(properties: Record<string, unknown>): void {
    if (!this.initialized) return;
    this.ngZone.runOutsideAngular(() => posthog.register(properties));
  }

  capture(event: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) return;
    this.ngZone.runOutsideAngular(() => posthog.capture(event, properties));
  }

  // Keep auth tokens and credentials out of captured network requests/replays.
  private redactNetworkRequest(
    request: CapturedNetworkRequest
  ): CapturedNetworkRequest {
    if (request.name) {
      request.name = request.name.replace(
        /([?&][^=&]*(?:token|auth|key|password|email|secret)[^=&]*=)[^&]+/gi,
        '$1[REDACTED]'
      );
    }
    this.redactHeaders(request.requestHeaders);
    this.redactHeaders(request.responseHeaders);
    if (
      request.name &&
      /identitytoolkit|securetoken|\/token\b/i.test(request.name)
    ) {
      if (request.requestBody) request.requestBody = '[REDACTED]';
      if (request.responseBody) request.responseBody = '[REDACTED]';
    }
    return request;
  }

  private redactHeaders(headers?: Record<string, string>): void {
    if (!headers) return;
    for (const key of Object.keys(headers)) {
      if (/authorization|cookie|token|api-?key|x-goog/i.test(key)) {
        headers[key] = '[REDACTED]';
      }
    }
  }
}
